import { useTranslation } from "react-i18next";

import { ApiError } from "@/shared/api/errors";
import { Button } from "@/shared/ui/Button";

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
}

/**
 * Affiche une erreur API de façon exploitable par l'utilisateur.
 *
 * On traduit le `code` renvoyé par l'API, jamais son `detail` (anglais, destiné
 * aux développeurs). Le `request_id` est montré : c'est ce que le support
 * demandera (docs/22 §5, docs/21 §1).
 */
export function ErrorState({ error, onRetry }: ErrorStateProps) {
  const { t } = useTranslation();
  const apiError = error instanceof ApiError ? error : null;
  const code = apiError?.code ?? "unexpected_error";

  return (
    <div
      role="alert"
      className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950"
    >
      <p className="font-medium text-red-900 dark:text-red-200">{t("errors.title")}</p>
      <p className="mt-1 text-sm text-red-800 dark:text-red-300">
        {t([`errors.${code}`, "errors.unexpected_error"])}
      </p>
      {apiError?.requestId !== undefined && (
        <p className="mt-2 font-mono text-xs text-red-700 dark:text-red-400">
          {t("errors.requestId", { id: apiError.requestId })}
        </p>
      )}
      {onRetry !== undefined && (
        <Button variant="secondary" className="mt-4" onClick={onRetry}>
          {t("api.retry")}
        </Button>
      )}
    </div>
  );
}
